import { SEO_DEFAULTS } from './seo';

export interface Article {
  id: string;
  title: string;
  excerpt?: string;
  content: string;
  slug: string;
  featured_image_url?: string;
  author_id?: string;
  published_at?: string;
  updated_at?: string;
  category?: { name: string; slug: string };
  tags?: Array<{ name: string; slug: string }>;
}

export interface Author {
  display_name: string;
  bio?: string;
  avatar_url?: string;
}

export const generateOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SEO_DEFAULTS.siteName,
  url: SEO_DEFAULTS.baseUrl,
  logo: `${SEO_DEFAULTS.baseUrl}/lovable-uploads/comprehensive-strategy.png`,
  description: 'AI-powered marketing platform for business owners and agencies.',
  sameAs: [
    'https://www.linkedin.com/company/storily',
    'https://twitter.com/storily_io',
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'Customer Service',
    email: 'support@storily.io',
  },
});

export const generateWebSiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SEO_DEFAULTS.siteName,
  url: SEO_DEFAULTS.baseUrl,
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${SEO_DEFAULTS.baseUrl}/blog?search={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
});

export const generateArticleSchema = (article: Article, author?: Author) => {
  const description = article.excerpt || article.content.substring(0, 155);
  const wordCount = article.content.replace(/<[^>]*>/g, '').split(/\s+/).filter(Boolean).length;
  
  const schema: any = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: description,
    url: `${SEO_DEFAULTS.baseUrl}/articles/${article.slug}`,
    datePublished: article.published_at || article.updated_at,
    dateModified: article.updated_at,
    wordCount: wordCount,
    inLanguage: 'en-US',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SEO_DEFAULTS.baseUrl}/articles/${article.slug}`
    },
    publisher: {
      '@type': 'Organization',
      name: SEO_DEFAULTS.siteName,
      url: SEO_DEFAULTS.baseUrl,
      logo: {
        '@type': 'ImageObject',
        url: `${SEO_DEFAULTS.baseUrl}/lovable-uploads/comprehensive-strategy.png`,
        width: 600,
        height: 60
      },
    },
  };

  if (article.featured_image_url) {
    schema.image = {
      '@type': 'ImageObject',
      url: article.featured_image_url,
      width: 1200,
      height: 630
    };
  }

  if (author) {
    schema.author = {
      '@type': 'Person',
      name: author.display_name,
      description: author.bio,
      image: author.avatar_url,
      url: `${SEO_DEFAULTS.baseUrl}/blog?author=${encodeURIComponent(author.display_name)}`
    };
  }

  if (article.category) {
    schema.articleSection = article.category.name;
  }

  if (article.tags && article.tags.length > 0) {
    schema.keywords = article.tags.map(t => t.name).join(', ');
  }

  return schema;
};

export const generateBreadcrumbSchema = (items: Array<{ name: string; url: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url,
  })),
});

export const generateBlogPostingListSchema = (articles: any[]) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Storily Blog Articles',
  description: 'Expert marketing tips, proven strategies, and actionable insights for business owners and agencies.',
  numberOfItems: articles.length,
  itemListElement: articles.map((article, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    item: {
      '@type': 'BlogPosting',
      '@id': `${SEO_DEFAULTS.baseUrl}/articles/${article.slug}`,
      url: `${SEO_DEFAULTS.baseUrl}/articles/${article.slug}`,
      headline: article.title,
      description: article.excerpt || '',
      datePublished: article.published_at,
      image: article.featured_image_url,
      author: {
        '@type': 'Person',
        name: article.authors?.display_name || 'Storily Team'
      }
    }
  })),
});

export interface HowToStep {
  name: string;
  text: string;
  image?: string;
}

export const generateHowToSchema = (
  name: string,
  description: string,
  steps: HowToStep[],
  totalTime?: string
) => ({
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name,
  description,
  ...(totalTime && { totalTime }),
  step: steps.map((step, index) => ({
    '@type': 'HowToStep',
    position: index + 1,
    name: step.name,
    text: step.text,
    ...(step.image && { image: step.image })
  }))
});

export const generateFAQSchema = (faqs: Array<{ question: string; answer: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
});

export const generateServiceSchema = (services: Array<{ name: string; description: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  provider: {
    '@type': 'Organization',
    name: SEO_DEFAULTS.siteName,
  },
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Marketing Services',
    itemListElement: services.map((service) => ({
      '@type': 'Offer',
      itemOffered: {
        '@type': 'Service',
        name: service.name,
        description: service.description,
      },
    })),
  },
});
